/**
 * The CAD1 launches as the pages list them (PLAN cad1-phase3 §6 S6, S7). Pure: takes a viem public client for chain 56, so the tests answer
 * with a fake. The launcher registry is the only source of what is a CAD1 launch:
 *   launchCount() and getLaunches(offset, limit)  which tokens, in launch order (#1 = index 0); the pages list them newest first;
 *   poolOf(token)                                 the CAD1 pool, the only price source (token0 is the token: it sorts below WBNB);
 *   isCad1Token(token)                            authenticity (the …cad1 ending proves nothing).
 * Row details come from the lock index (/api/locks/token/<token>, LockTools' server), never from eth_getLogs: the public BSC RPC refuses log
 * ranges ("limit exceeded", even for 1,000 blocks; Bez's live test 2026-10-09). Every launch locks its position for LOCK_DURATION in the
 * CADINU V3 lock in the launch transaction, for msg.sender, so the launch lock (the CAD1 pool's first lock of exactly that duration) gives the
 * creator (the lock owner), the lock id and date and the launch time (the lock date). What only the Launched event had (the share bought at
 * launch, the first buy) is shown only where the event is at hand (the tx page reads it from its own receipt), never guessed; a row whose
 * lock is not in the index keeps the registry facts and says its details were not read.
 *
 * Two launchers (Bez 2026-10-09): v2 takes every new launch, v1 is retired by setPaused and its tokens stay valid. Each keeps its own
 * registry, so the list reads both as one newest-first list (combinedSlices): v1's launches are #1..#n1 and v2's continue from #n1+1. Every
 * row carries the launcher that registered it, and its pool and number are read from that launcher only.
 */
import { getAddress, isAddress, type Address } from 'viem'
import { cad1LauncherAbi } from '../abi/cad1Launcher'
import { cad1PoolAbi } from '../abi/reads'
import { cad1TokenAbi } from '../abi/cad1Token'
import { CAD1_PLANNED, CAD1_WIRING, type Cad1Registry } from '../config'
import type { Cad1Launch } from './receipt'

export const PAGE_SIZE = 24

type Mc = { status: 'success'; result: unknown } | { status: 'failure'; error?: unknown }
/** what the reads need from a public client: contract reads only (no eth_getLogs anywhere) */
export type LaunchesClient = {
  readContract: (args: any) => Promise<unknown>
  multicall: (args: { contracts: readonly any[]; allowFailure: true; blockNumber?: bigint }) => Promise<readonly Mc[]>
}

export type LaunchRow = {
  /** index in the combined list (v1's launches first, then v2's); the launch number is index + 1 */
  index: number
  token: Address
  /** the launcher whose registry holds the token (v1 or v2) */
  launcher?: Address
  pool?: Address
  name?: string
  symbol?: string
  /** the CAD1 pool's slot0 sqrt price (token0 = the token, priced in WBNB) */
  sqrtPriceX96?: bigint
  feeProtocol?: number
  /** WBNB.balanceOf(pool): the BNB really in the CAD1 pool (at launch only the first buy; review H1) */
  poolBnb?: bigint
  /** from the Launched event, only where one is at hand (never read by eth_getLogs) */
  launch?: Cad1Launch
  /** the launch lock, from the lock index: creator (its owner), lock id, launch time; undefined when the index did not have it */
  lock?: Cad1LockFacts
  /** the creator's balance now, in bps of the supply (read only when the lock index named the creator) */
  holdsBps?: bigint
}

/** What the lock index says about a launch: its 10-year lock of the launch position, made in the launch transaction for msg.sender. */
export type Cad1LockFacts = {
  lockId: bigint
  /** the lock owner, which the launcher sets to msg.sender (the creator); undefined once renounced */
  creator?: Address
  /** the lock date: the launch block's time */
  lockedAt: bigint
  unlockAt: bigint
}

export type FetchJson = (url: string) => Promise<unknown>

/** The lock index of one token (LockTools' /api/locks/token/<address>); same origin, cached by the server. */
export const lockIndexUrl = (token: string) => `/api/locks/token/${token.toLowerCase()}`

type IndexLock = { type?: unknown; id?: unknown; kind?: unknown; contract?: unknown; owner?: unknown; renounced?: unknown; lockedAt?: unknown; unlockAt?: unknown; position?: { pool?: unknown; manager?: unknown } }
const lower = (v: unknown) => (typeof v === 'string' ? v.toLowerCase() : '')
const ZERO = /^0x0{40}$/i

/**
 * The launch lock among a token's locks in the index: a V3 position lock in the CADINU V3 lock, of the CAD1 pool and the CADINU position
 * manager; of those, one of exactly LOCK_DURATION first, then the earliest (the launch made the pool's first position). Null when none.
 */
export function lockFactsFrom(index: unknown, pool: string): Cad1LockFacts | null {
  const locks = (index && typeof index === 'object' && Array.isArray((index as { locks?: unknown }).locks) ? (index as { locks: IndexLock[] }).locks : []).filter(
    (l) =>
      l &&
      lower(l.type) === 'v3' &&
      lower(l.kind) === 'v3-position' &&
      lower(l.contract) === CAD1_WIRING.lock.toLowerCase() &&
      lower(l.position?.pool) === pool.toLowerCase() &&
      lower(l.position?.manager) === CAD1_WIRING.positionManager.toLowerCase() &&
      Number.isSafeInteger(l.id) &&
      Number.isSafeInteger(l.lockedAt) &&
      Number.isSafeInteger(l.unlockAt),
  )
  if (!locks.length) return null
  const exact = (l: IndexLock) => BigInt(l.unlockAt as number) - BigInt(l.lockedAt as number) === CAD1_PLANNED.lockDuration
  const best = locks.slice().sort((a, b) => Number(exact(b)) - Number(exact(a)) || (a.lockedAt as number) - (b.lockedAt as number) || (a.id as number) - (b.id as number))[0]
  const owner = typeof best.owner === 'string' && isAddress(best.owner) && !ZERO.test(best.owner) && best.renounced !== true ? getAddress(best.owner) : undefined
  return { lockId: BigInt(best.id as number), lockedAt: BigInt(best.lockedAt as number), unlockAt: BigInt(best.unlockAt as number), ...(owner ? { creator: owner } : {}) }
}

/** The launch lock of `token` in its CAD1 `pool`, from the lock index; null when the index has none (a failed fetch throws). */
export async function readLockFacts(fetchJson: FetchJson, token: string, pool: string): Promise<Cad1LockFacts | null> {
  if (!pool || ZERO.test(pool)) return null
  return lockFactsFrom(await fetchJson(lockIndexUrl(token)), pool)
}

/** Lock facts (per row; a failed index read leaves that row without them) and, where the creator is known, the creator's holdings now. */
export async function addLockFacts(pc: Pick<LaunchesClient, 'multicall'>, fetchJson: FetchJson, rows: readonly LaunchRow[]): Promise<LaunchRow[]> {
  const facts = await Promise.all(rows.map((r) => (r.pool ? readLockFacts(fetchJson, r.token, r.pool).catch(() => null) : Promise.resolve(null))))
  const out = rows.map((r, i) => (facts[i] ? { ...r, lock: facts[i] as Cad1LockFacts } : { ...r }))
  const held = out.filter((r) => r.lock?.creator)
  if (held.length) {
    const b = await pc.multicall({ allowFailure: true, contracts: held.map((r) => ({ address: r.token, abi: cad1TokenAbi, functionName: 'balanceOf', args: [r.lock?.creator] })) }).catch(() => [] as Mc[])
    held.forEach((r, i) => {
      const v = val<bigint>(b[i])
      // eslint-disable-next-line no-param-reassign
      if (v !== undefined) r.holdsBps = (v * CAD1_PLANNED.bps) / CAD1_PLANNED.totalSupply
    })
  }
  return out
}

/** The launch time of a row: the Launched event's, else the lock date from the index; null when neither was read. */
export const rowLaunchedAt = (row: Pick<LaunchRow, 'launch' | 'lock'>): bigint | null => (row.launch ? launchedAt(row.launch) : row.lock ? row.lock.lockedAt : null)
/** The creator of a row: the Launched event's, else the launch lock's owner; undefined when neither was read. */
export const rowCreator = (row: Pick<LaunchRow, 'launch' | 'lock'>): Address | undefined => row.launch?.creator ?? row.lock?.creator
/** High creator share: the event's flag or share at launch; without the event, the creator's holdings now (20 % or more). */
export const rowHighShare = (row: Pick<LaunchRow, 'launch' | 'holdsBps'>): boolean =>
  row.launch ? row.launch.info.highCreatorShare || row.launch.info.creatorShareBps >= CAD1_PLANNED.highShareBps : row.holdsBps !== undefined && row.holdsBps >= CAD1_PLANNED.highShareBps

const val = <T>(r: Mc | undefined): T | undefined => (r && r.status === 'success' ? (r.result as T) : undefined)
const Q192 = 2n ** 192n

/** The token's value at the pool price, in BNB wei: price (WBNB per token, token0) × supply. Never inverted. */
export const marketValueWei = (sqrtPriceX96: bigint, supply: bigint = CAD1_PLANNED.totalSupply): bigint => (sqrtPriceX96 * sqrtPriceX96 * supply) / Q192
/** BNB wei per whole token at a sqrt price. */
export const pricePerTokenWei = (sqrtPriceX96: bigint): bigint => (sqrtPriceX96 * sqrtPriceX96 * 10n ** 18n) / Q192

/** The launch time: the lock was made in the same block for exactly LOCK_DURATION. */
export const launchedAt = (launch: Cad1Launch): bigint => launch.info.unlockAt - CAD1_PLANNED.lockDuration

/** Which registry slice holds page `page` (0 = newest), newest first. */
export function pageRange(count: bigint, page: number, size: number = PAGE_SIZE): { offset: bigint; limit: bigint } | null {
  const end = count - BigInt(page * size)
  if (end <= 0n) return null
  const offset = end > BigInt(size) ? end - BigInt(size) : 0n
  return { offset, limit: end - offset }
}

/** One page of the registry, newest first, with pool, name, symbol and pool price (no events). */
export async function readLaunchPage(pc: LaunchesClient, launcher: Address, page = 0, size = PAGE_SIZE): Promise<{ count: bigint; rows: LaunchRow[] }> {
  return readCombinedLaunchPage(pc, [{ address: launcher }], page, size)
}

/**
 * Which slice of each registry holds page `page` (0 = newest) of the combined list, newest first. `counts` are the registries' launch
 * counts in registry order (oldest launcher first); a registry's launches are numbered after every earlier registry's (`base`).
 */
export function combinedSlices(counts: readonly bigint[], page: number, size: number = PAGE_SIZE): { registry: number; offset: bigint; limit: bigint; base: bigint }[] {
  const total = counts.reduce((a, b) => a + b, 0n)
  const end = total - BigInt(page * size)
  if (end <= 0n) return []
  const start = end > BigInt(size) ? end - BigInt(size) : 0n
  const out: { registry: number; offset: bigint; limit: bigint; base: bigint }[] = []
  let base = 0n
  counts.forEach((count, registry) => {
    const lo = start > base ? start : base
    const hi = end < base + count ? end : base + count
    if (hi > lo) out.push({ registry, offset: lo - base, limit: hi - lo, base })
    base += count
  })
  return out.reverse()
}

/** Each row's CAD1 pool (from its own launcher), name and symbol, in one multicall (filled in place). */
async function readRowBasics(pc: Pick<LaunchesClient, 'multicall'>, rows: LaunchRow[]): Promise<void> {
  const r = await pc.multicall({
    allowFailure: true,
    contracts: rows.flatMap((row) => [
      { address: row.launcher, abi: cad1LauncherAbi, functionName: 'poolOf', args: [row.token] },
      { address: row.token, abi: cad1TokenAbi, functionName: 'name' },
      { address: row.token, abi: cad1TokenAbi, functionName: 'symbol' },
    ]),
  })
  rows.forEach((row, i) => {
    /* eslint-disable no-param-reassign */
    const pool = val<Address>(r[i * 3])
    row.pool = pool && !ZERO.test(pool) ? pool : undefined
    row.name = val<string>(r[i * 3 + 1])
    row.symbol = val<string>(r[i * 3 + 2])
    /* eslint-enable no-param-reassign */
  })
}

/** One page of the combined registries (v1 then v2), newest first, each row read from its own launcher (no events). */
export async function readCombinedLaunchPage(
  pc: LaunchesClient,
  registries: readonly Pick<Cad1Registry, 'address'>[],
  page = 0,
  size = PAGE_SIZE,
): Promise<{ count: bigint; rows: LaunchRow[] }> {
  const counts = (await Promise.all(registries.map((r) => pc.readContract({ address: r.address, abi: cad1LauncherAbi, functionName: 'launchCount' })))) as bigint[]
  const count = counts.reduce((a, b) => a + b, 0n)
  const slices = combinedSlices(counts, page, size)
  const rows: LaunchRow[] = []
  for (const slice of slices) {
    const launcher = registries[slice.registry].address
    // eslint-disable-next-line no-await-in-loop
    const tokens = ((await pc.readContract({ address: launcher, abi: cad1LauncherAbi, functionName: 'getLaunches', args: [slice.offset, slice.limit] })) as readonly Address[]).slice()
    const first = Number(slice.base + slice.offset)
    rows.push(...tokens.map((token, i) => ({ index: first + i, token, launcher })).reverse())
  }
  if (!rows.length) return { count, rows }
  await readRowBasics(pc, rows)
  const withPool = rows.filter((row) => row.pool && !/^0x0{40}$/i.test(row.pool))
  if (withPool.length) {
    const s = await pc.multicall({
      allowFailure: true,
      contracts: withPool.flatMap((row) => [
        { address: row.pool, abi: cad1PoolAbi, functionName: 'slot0' },
        { address: CAD1_WIRING.wbnb, abi: cad1TokenAbi, functionName: 'balanceOf', args: [row.pool] },
      ]),
    })
    withPool.forEach((row, i) => {
      const slot = val<readonly [bigint, number, number, number, number, number, boolean]>(s[i * 2])
      /* eslint-disable no-param-reassign */
      row.sqrtPriceX96 = slot?.[0]
      row.feeProtocol = slot === undefined ? undefined : Number(slot[5])
      row.poolBnb = val<bigint>(s[i * 2 + 1])
      /* eslint-enable no-param-reassign */
    })
  }
  return { count, rows }
}

/**
 * The launch number of `token` from the registries as they are now (no historical state, no logs): every earlier registry's launchCount
 * plus the token's place in its own launcher's getLaunches, read newest first in pages of `page` (at most `maxPages`). Null when the
 * launcher is not configured or the token was not found.
 */
export async function readRegistryNumber(
  pc: Pick<LaunchesClient, 'readContract'>,
  registries: readonly Pick<Cad1Registry, 'address'>[],
  launcher: Address,
  token: Address,
  { page = 200n, maxPages = 25 }: { page?: bigint; maxPages?: number } = {},
): Promise<bigint | null> {
  const at = registries.findIndex((r) => r.address.toLowerCase() === launcher.toLowerCase())
  if (at === -1) return null
  const own = registries[at].address
  const counts = (await Promise.all(registries.slice(0, at + 1).map((r) => pc.readContract({ address: r.address, abi: cad1LauncherAbi, functionName: 'launchCount' })))) as bigint[]
  const earlier = counts.slice(0, at).reduce((a, b) => a + b, 0n)
  const want = token.toLowerCase()
  let end = counts[at]
  for (let n = 0; n < maxPages && end > 0n; n++) {
    const offset = end > page ? end - page : 0n
    // eslint-disable-next-line no-await-in-loop
    const tokens = (await pc.readContract({ address: own, abi: cad1LauncherAbi, functionName: 'getLaunches', args: [offset, end - offset] })) as readonly string[]
    const i = tokens.findIndex((t) => t.toLowerCase() === want)
    if (i !== -1) return earlier + offset + BigInt(i) + 1n
    end = offset
  }
  return null
}

/**
 * The newest launch by `creator`, matched by the launch lock's owner in the lock index (no logs): among the combined registries' newest
 * `max` launches, or, with `since`, among that launcher's launches from that launchCount on (the count read before a send: a launch sent
 * through a smart account, a batch or an EIP-7702 route, or a wallet that answered with a hash the node does not know). Null when none.
 */
export async function findLaunchBy(
  pc: LaunchesClient,
  fetchJson: FetchJson,
  registries: readonly Pick<Cad1Registry, 'address'>[],
  creator: Address,
  { since, max = 8 }: { since?: { launcher: Address; count: bigint }; max?: number } = {},
): Promise<LaunchRow | null> {
  let rows: LaunchRow[]
  if (since) {
    const reg = registries.find((r) => r.address.toLowerCase() === since.launcher.toLowerCase())
    if (!reg) return null
    const count = (await pc.readContract({ address: reg.address, abi: cad1LauncherAbi, functionName: 'launchCount' })) as bigint
    if (count <= since.count) return null
    const offset = count - since.count > BigInt(max) ? count - BigInt(max) : since.count
    const tokens = (await pc.readContract({ address: reg.address, abi: cad1LauncherAbi, functionName: 'getLaunches', args: [offset, count - offset] })) as readonly Address[]
    rows = tokens.map((token, i) => ({ index: Number(offset) + i, token, launcher: reg.address })).reverse()
    if (rows.length) await readRowBasics(pc, rows)
  } else {
    ;({ rows } = await readCombinedLaunchPage(pc, registries, 0, max))
  }
  const facts = await Promise.all(rows.map((r) => (r.pool ? readLockFacts(fetchJson, r.token, r.pool).catch(() => null) : Promise.resolve(null))))
  const i = facts.findIndex((f) => f?.creator && f.creator.toLowerCase() === creator.toLowerCase())
  return i === -1 ? null : { ...rows[i], lock: facts[i] as Cad1LockFacts }
}

/**
 * Pools that moved since `sinceSeconds` (the Active 24h tab), with no logs: a CADINU V3 pool (PancakeSwap V3 code) writes an oracle
 * observation in the first swap of a block that moves its tick, and on a liquidity change in range, so the newest observation's time
 * (observations(slot0.observationIndex).blockTimestamp) is when the pool last moved. A swap too small to move the tick is not seen.
 */
export async function readActivePools(pc: Pick<LaunchesClient, 'multicall'>, pools: readonly Address[], sinceSeconds: bigint): Promise<Set<string>> {
  const active = new Set<string>()
  if (!pools.length) return active
  const s = await pc.multicall({ allowFailure: true, contracts: pools.map((p) => ({ address: p, abi: cad1PoolAbi, functionName: 'slot0' })) })
  const at = pools.map((p, i) => ({ pool: p, index: val<readonly unknown[]>(s[i])?.[2] })).filter((x): x is { pool: Address; index: number } => typeof x.index === 'number')
  if (!at.length) return active
  const o = await pc.multicall({ allowFailure: true, contracts: at.map((x) => ({ address: x.pool, abi: cad1PoolAbi, functionName: 'observations', args: [BigInt(x.index)] })) })
  at.forEach((x, i) => {
    const obs = val<readonly unknown[]>(o[i])
    if (obs && obs[3] === true && BigInt(obs[0] as number) >= sinceSeconds) active.add(x.pool.toLowerCase())
  })
  return active
}

/** Filter rows by a search text: name, symbol or address (case-insensitive, a leading $ ignored). */
export function searchRows(rows: readonly LaunchRow[], q: string): LaunchRow[] {
  const s = q.trim().replace(/^\$/, '').toLowerCase()
  if (!s) return rows.slice()
  return rows.filter((r) => r.token.toLowerCase().includes(s) || (r.name ?? '').toLowerCase().includes(s) || (r.symbol ?? '').toLowerCase().includes(s))
}
